import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import baseURL from '../../url';
import moneda from '../../moneda';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBell,
    faCheckCircle,
    faUtensils,
    faClock,
    faSync,
    faPlus,
    faTrash,
    faArrowLeft,
    faArrowRight,
    faFire,
    faSearch,
    faTimes,
    faReceipt,
    faUserTie,
    faExclamationCircle,
    faCommentDots,
    faCheck
} from '@fortawesome/free-solid-svg-icons';
import { playSound } from '../../../utils/soundService';
import { getConfig, fetchConfigFromDB } from '../../../utils/configService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import './MeserosData.css';

// Comparador rápido para evitar re-renders si el backend devuelve los mismos datos
const sonDatosIguales = (a, b) => {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    return JSON.stringify(a) === JSON.stringify(b);
};

// Componente memoizado para la tarjeta de plato en el catálogo principal
const MeseroDishCard = React.memo(function MeseroDishCard({
    prod,
    categoriaNombre,
    usarPrecios,
    cantidadEnCart,
    onAgregar,
    onModificar
}) {
    return (
        <div className="mesero-dish-card">
            <div className="dish-img-wrap">
                {prod.imagen1 ? (
                    <img src={prod.imagen1} alt={prod.titulo} loading="lazy" />
                ) : (
                    <FontAwesomeIcon icon={faUtensils} className="dish-placeholder" />
                )}
            </div>

            <div className="dish-info-wrap">
                <div>
                    <span className="dish-category-tag">
                        {categoriaNombre}
                    </span>
                    <h4 className="dish-title">{prod.titulo}</h4>
                    {prod.descripcion && (
                        <p className="dish-desc">{prod.descripcion}</p>
                    )}
                </div>

                <div className="dish-bottom-row">
                    {usarPrecios ? (
                        <span className="dish-price">{moneda} {prod.precio}</span>
                    ) : <span></span>}

                    {cantidadEnCart > 0 ? (
                        <div className="mesero-qty-stepper">
                            <button type="button" onClick={() => onModificar(prod.idProducto, -1)}>-</button>
                            <span className="stepper-val">{cantidadEnCart}</span>
                            <button type="button" onClick={() => onModificar(prod.idProducto, 1)}>+</button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="btn-mesero-add-dish"
                            onClick={() => onAgregar(prod)}
                        >
                            <FontAwesomeIcon icon={faPlus} /> Agregar
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
});

// Componente memoizado para plato en la sección Más Vendidos
const MeseroBestsellerCard = React.memo(function MeseroBestsellerCard({
    prod,
    usarPrecios,
    cantidadEnCart,
    onAgregar,
    onModificar
}) {
    return (
        <div className="mesero-bestseller-card">
            <div className="bestseller-img-wrap">
                {prod.imagen1 ? (
                    <img src={prod.imagen1} alt={prod.titulo} loading="lazy" />
                ) : (
                    <FontAwesomeIcon icon={faUtensils} className="dish-placeholder" />
                )}
            </div>
            <div className="bestseller-info">
                <h4>{prod.titulo}</h4>
                <div className="bestseller-bottom">
                    {usarPrecios && (
                        <span className="bestseller-price">{moneda} {prod.precio}</span>
                    )}
                    {cantidadEnCart > 0 ? (
                        <div className="mesero-qty-stepper">
                            <button type="button" onClick={() => onModificar(prod.idProducto, -1)}>-</button>
                            <span className="stepper-val">{cantidadEnCart}</span>
                            <button type="button" onClick={() => onModificar(prod.idProducto, 1)}>+</button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="btn-mesero-add-dish"
                            onClick={() => onAgregar(prod)}
                        >
                            <FontAwesomeIcon icon={faPlus} /> Pedir
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
});

// Componente memoizado para tarjeta de mesa en pantalla principal
const MeseroMesaCard = React.memo(function MeseroMesaCard({
    m,
    pedidosActivosMesa,
    tieneLlamada,
    llamadaActiva,
    estaOcupada,
    itemsEnCarrito,
    onSeleccionar,
    onLiberar
}) {
    return (
        <div
            className={`mesero-mesa-card ${estaOcupada ? 'mesa-ocupada' : 'mesa-libre'} ${tieneLlamada ? 'mesa-radar-pulse' : ''}`}
            onClick={() => onSeleccionar(m)}
        >
            {/* Cabecera de la tarjeta */}
            <div className="mesa-card-top-row">
                <div className="mesa-title-wrap">
                    <h4>{m.mesa}</h4>
                    {itemsEnCarrito > 0 && (
                        <span className="mesa-badge-draft-cart" title="Comanda en borrador">
                            🛒 {itemsEnCarrito} en comanda
                        </span>
                    )}
                </div>
                <span className={`mesa-status-tag ${estaOcupada ? 'tag-ocupada' : 'tag-libre'}`}>
                    {estaOcupada ? 'Ocupada' : 'Disponible'}
                </span>
            </div>

            {/* Alerta de llamada al mesero con efecto radar */}
            {tieneLlamada && (
                <div className="mesa-calling-badge">
                    <FontAwesomeIcon icon={faBell} className="bell-pulse" />
                    <span>¡Solicita atención! ({llamadaActiva?.motivo || 'Llamada'})</span>
                </div>
            )}

            {/* Resumen de consumo activo */}
            {pedidosActivosMesa.length > 0 ? (
                <div className="mesa-order-preview">
                    <div className="order-summary-row">
                        <span className="order-id-label">
                            <FontAwesomeIcon icon={faReceipt} /> {pedidosActivosMesa.length} {pedidosActivosMesa.length === 1 ? 'pedido activo' : 'pedidos activos'}
                        </span>
                        <span className="order-state-label">
                            {pedidosActivosMesa[0].estado}
                        </span>
                    </div>
                    <div className="order-total-highlight">
                        Total acumulado: <strong>{moneda} {pedidosActivosMesa.reduce((acc, p) => acc + (parseFloat(p.total) || 0), 0).toFixed(2)}</strong>
                    </div>
                </div>
            ) : (
                <div className="mesa-empty-preview">
                    Mesa sin pedidos activos
                </div>
            )}

            {/* Botones de acción rápida */}
            <div className="mesa-card-bottom-actions" onClick={(e) => e.stopPropagation()}>
                <button
                    type="button"
                    className="btn-tomar-pedido-mesa"
                    onClick={() => onSeleccionar(m)}
                >
                    <FontAwesomeIcon icon={faUtensils} />
                    <span>{estaOcupada ? 'Ver / Agregar Comanda' : 'Tomar Pedido'}</span>
                </button>

                {estaOcupada && (
                    <button
                        type="button"
                        className="btn-liberar-mesa-mesero"
                        onClick={() => onLiberar(m)}
                        title="Desocupar y liberar mesa"
                    >
                        <FontAwesomeIcon icon={faCheckCircle} /> Liberar
                    </button>
                )}
            </div>
        </div>
    );
});

export default function MeserosData() {
    // Vistas: 'mesas' (pantalla inicial) o 'menu' (carta digital de la mesa seleccionada)
    const [vistaActiva, setVistaActiva] = useState('mesas');
    const [mesaSeleccionada, setMesaSeleccionada] = useState(null);

    // Datos principales
    const [mesas, setMesas] = useState([]);
    const [pedidos, setPedidos] = useState([]);
    const [productos, setProductos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [llamadas, setLlamadas] = useState([]);

    // Carrito de comanda guardado por mesa: { [idMesa]: [ { idProducto, titulo, precio, cantidad, nota, ... } ] }
    const [carritosPorMesa, setCarritosPorMesa] = useState({});
    const [nombreCliente, setNombreCliente] = useState('');
    const [notaCocinaGeneral, setNotaCocinaGeneral] = useState('');
    const [drawerComandaAbierto, setDrawerComandaAbierto] = useState(false);
    const [modalVerConsumo, setModalVerConsumo] = useState(false);
    const [guardandoPedido, setGuardandoPedido] = useState(false);

    // Filtros de menú digital
    const [categoriaActiva, setCategoriaActiva] = useState('todas');
    const [busqueda, setBusqueda] = useState('');

    // Modalidad de precios
    const [usarPrecios, setUsarPrecios] = useState(getConfig().usarPrecios !== 'no');

    // Referencias para evitar re-creación de funciones y dependencias cíclicas
    const prevCallIdsRef = useRef(new Set());
    const isFirstLoadRef = useRef(true);
    const mesasRef = useRef(mesas);
    const abrirMesaPorIdRef = useRef(null);

    useEffect(() => {
        mesasRef.current = mesas;
    }, [mesas]);

    // Carrito de la mesa seleccionada actualmente
    const cartActual = useMemo(() => {
        if (!mesaSeleccionada?.idMesa) return [];
        return carritosPorMesa[mesaSeleccionada.idMesa] || [];
    }, [carritosPorMesa, mesaSeleccionada]);

    // Mapa de cantidad por idProducto en el carrito actual para lookup rápido O(1)
    const cantidadesCartMap = useMemo(() => {
        const map = {};
        cartActual.forEach(item => {
            map[item.idProducto] = item.cantidad;
        });
        return map;
    }, [cartActual]);

    // Totales del carrito actual
    const totalItemsCart = useMemo(() => {
        return cartActual.reduce((acc, it) => acc + (it.cantidad || 0), 0);
    }, [cartActual]);

    const totalDineroCart = useMemo(() => {
        return cartActual.reduce((acc, it) => acc + ((Number(it.precio) || 0) * (it.cantidad || 0)), 0);
    }, [cartActual]);

    // Mapeo de categorías por idCategoria
    const mapaCategorias = useMemo(() => {
        const map = {};
        categorias.forEach(c => {
            map[String(c.idCategoria)] = c.categoria;
        });
        return map;
    }, [categorias]);

    // Categorías disponibles que tienen productos
    const categoriasDisponibles = useMemo(() => {
        return categorias.filter(c =>
            productos.some(p => String(p.idCategoria) === String(c.idCategoria))
        );
    }, [categorias, productos]);

    // Productos más vendidos / populares
    const masVendidos = useMemo(() => {
        return productos.filter(p => p.masVendido === 'si' || p.masVendido === '1' || p.masVendido === 'true').slice(0, 10);
    }, [productos]);

    // Productos filtrados para el catálogo del mesero
    const productosFiltrados = useMemo(() => {
        return productos.filter(p => {
            const nombreCat = mapaCategorias[String(p.idCategoria)] || '';
            const query = busqueda.toLowerCase().trim();
            const coincideTexto = !query ||
                p.titulo?.toLowerCase().includes(query) ||
                p.descripcion?.toLowerCase().includes(query) ||
                nombreCat.toLowerCase().includes(query);
            const coincideCat = categoriaActiva === 'todas' || String(p.idCategoria) === String(categoriaActiva);
            return coincideTexto && coincideCat;
        });
    }, [productos, busqueda, categoriaActiva, mapaCategorias]);

    // Carga de datos con protección anti-parpadeo (solo actualiza el estado si cambió)
    const cargarMesas = useCallback(async () => {
        try {
            const res = await fetch(`${baseURL}/mesaGet.php`);
            const data = await res.json();
            const incoming = data.mesas || [];

            setMesas(prev => {
                if (sonDatosIguales(prev, incoming)) return prev;
                return incoming;
            });

            // Mantener sincronizada la mesa seleccionada si sus datos cambian
            setMesaSeleccionada(current => {
                if (!current) return null;
                const actualizada = incoming.find(m => String(m.idMesa) === String(current.idMesa));
                if (actualizada && (actualizada.estado !== current.estado || actualizada.mesa !== current.mesa)) {
                    return actualizada;
                }
                return current;
            });
        } catch (e) {
            console.error('Error al cargar mesas:', e);
        }
    }, []);

    const cargarPedidos = useCallback(async () => {
        try {
            const res = await fetch(`${baseURL}/pedidoGet.php`);
            const data = await res.json();
            const incoming = data.pedidos || [];

            setPedidos(prev => {
                if (sonDatosIguales(prev, incoming)) return prev;
                return incoming;
            });
        } catch (e) {
            console.error('Error al cargar pedidos:', e);
        }
    }, []);

    const cargarProductos = useCallback(async () => {
        try {
            const res = await fetch(`${baseURL}/productosGet.php`);
            const data = await res.json();
            const incoming = data.productos || [];

            setProductos(prev => {
                if (sonDatosIguales(prev, incoming)) return prev;
                return incoming;
            });
        } catch (e) {
            console.error('Error al cargar productos:', e);
        }
    }, []);

    const cargarCategorias = useCallback(async () => {
        try {
            const res = await fetch(`${baseURL}/categoriasGet.php`);
            const data = await res.json();
            const incoming = data.categorias || [];

            setCategorias(prev => {
                if (sonDatosIguales(prev, incoming)) return prev;
                return incoming;
            });
        } catch (e) {
            console.error('Error al cargar categorías:', e);
        }
    }, []);

    // Seleccionar y abrir vista de comanda/menú para una mesa
    const seleccionarMesa = useCallback((mesa) => {
        setMesaSeleccionada(mesa);
        setNombreCliente(`Cliente ${mesa.mesa}`);
        setVistaActiva('menu');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const abrirMesaPorId = useCallback((idMesa) => {
        const currentMesas = mesasRef.current || [];
        const found = currentMesas.find(m => String(m.idMesa) === String(idMesa));
        if (found) {
            seleccionarMesa(found);
        } else {
            cargarMesas().then(() => {
                const refreshed = (mesasRef.current || []).find(m => String(m.idMesa) === String(idMesa));
                if (refreshed) seleccionarMesa(refreshed);
            });
        }
    }, [cargarMesas, seleccionarMesa]);

    useEffect(() => {
        abrirMesaPorIdRef.current = abrirMesaPorId;
    }, [abrirMesaPorId]);

    // Carga de llamadas pendientes con verificación sin dependencias circulares
    const cargarLlamadas = useCallback(async () => {
        try {
            const res = await fetch(`${baseURL}/llamadaMeseroGet.php?estado=pendiente`);
            const data = await res.json();
            const pendientes = data.llamadas || [];

            // Detectar si hay llamadas nuevas para reproducir sonido
            if (!isFirstLoadRef.current) {
                const nuevas = pendientes.filter(l => !prevCallIdsRef.current.has(l.idLlamada));
                if (nuevas.length > 0) {
                    const cfg = getConfig();
                    playSound(cfg.sonidoLlamadaMesero || 'beep.mp3');
                    toast.info(`🔔 ¡Llamada de cliente en ${nuevas[0].mesa}! Motivo: ${nuevas[0].motivo || 'Atención en mesa'}`, {
                        position: 'top-right',
                        autoClose: 6000,
                        onClick: () => {
                            if (abrirMesaPorIdRef.current) {
                                abrirMesaPorIdRef.current(nuevas[0].idMesa);
                            }
                        }
                    });
                }
            } else {
                isFirstLoadRef.current = false;
            }

            prevCallIdsRef.current = new Set(pendientes.map(l => l.idLlamada));
            setLlamadas(prev => {
                if (sonDatosIguales(prev, pendientes)) return prev;
                return pendientes;
            });
        } catch (e) {
            console.error('Error al cargar llamadas:', e);
        }
    }, []);

    // Ciclo de vida principal: Carga inicial y sondeo periódico estable
    useEffect(() => {
        // Carga inicial completa
        cargarMesas();
        cargarPedidos();
        cargarProductos();
        cargarCategorias();
        cargarLlamadas();

        fetchConfigFromDB().then(cfg => {
            if (cfg && cfg.usarPrecios !== undefined) {
                setUsarPrecios(cfg.usarPrecios !== 'no');
            }
        });

        const handleConfigChange = (e) => {
            if (e.detail && e.detail.usarPrecios !== undefined) {
                setUsarPrecios(e.detail.usarPrecios !== 'no');
            }
        };
        window.addEventListener('configUpdated', handleConfigChange);

        // Sondeo cada 5 segundos ÚNICAMENTE para llamadas, pedidos y estados de mesas (NO productos ni categorías)
        const interval = setInterval(() => {
            cargarLlamadas();
            cargarPedidos();
            cargarMesas();
        }, 5000);

        return () => {
            clearInterval(interval);
            window.removeEventListener('configUpdated', handleConfigChange);
        };
    }, [cargarMesas, cargarPedidos, cargarProductos, cargarCategorias, cargarLlamadas]);

    // Reproducir sonido sutil al agregar producto en comanda
    const reproducirSonidoAgregar = () => {
        try {
            const cfg = getConfig();
            const soundFile = cfg.sonidoAgregarProductoMesero || 'beep.mp3';
            playSound(soundFile);
        } catch (e) {
            console.error('Error al reproducir sonido:', e);
        }
    };

    // Atender llamada de cliente
    const atenderLlamada = async (idLlamada) => {
        try {
            const res = await fetch(`${baseURL}/llamadaMeseroPut.php?idLlamada=${idLlamada}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ estado: 'atendida' })
            });
            const data = await res.json();
            if (data.mensaje) {
                toast.success('Solicitud marcada como atendida');
                setLlamadas(prev => prev.filter(l => l.idLlamada !== idLlamada));
            } else {
                toast.error(data.error || 'Error al actualizar llamada');
            }
        } catch (e) {
            console.error(e);
            toast.error('Error de red al atender solicitud');
        }
    };

    // Regresar a la vista de mesas conservando el carrito
    const volverAMesas = () => {
        setVistaActiva('mesas');
        setDrawerComandaAbierto(false);
    };

    // Agregar producto al carrito de la mesa seleccionada
    const agregarAlCarritoMesa = useCallback((prod) => {
        if (!mesaSeleccionada) return;
        const idMesa = mesaSeleccionada.idMesa;
        reproducirSonidoAgregar();

        setCarritosPorMesa(prev => {
            const actualCart = prev[idMesa] || [];
            const existente = actualCart.find(it => it.idProducto === prod.idProducto);

            if (existente) {
                return {
                    ...prev,
                    [idMesa]: actualCart.map(it =>
                        it.idProducto === prod.idProducto
                            ? { ...it, cantidad: it.cantidad + 1 }
                            : it
                    )
                };
            }

            return {
                ...prev,
                [idMesa]: [
                    ...actualCart,
                    {
                        idProducto: prod.idProducto,
                        titulo: prod.titulo,
                        precio: Number(prod.precio) || 0,
                        cantidad: 1,
                        categoria: mapaCategorias[String(prod.idCategoria)] || '',
                        imagen: prod.imagen1 || prod.imagen2 || '',
                        nota: ''
                    }
                ]
            };
        });
    }, [mesaSeleccionada, mapaCategorias]);

    // Modificar cantidad en carrito
    const modificarCantidadMesa = useCallback((idProducto, delta) => {
        if (!mesaSeleccionada) return;
        const idMesa = mesaSeleccionada.idMesa;

        if (delta > 0) {
            reproducirSonidoAgregar();
        }

        setCarritosPorMesa(prev => {
            const actualCart = prev[idMesa] || [];
            const nuevoCart = actualCart.map(it => {
                if (it.idProducto === idProducto) {
                    const nuevaCant = it.cantidad + delta;
                    return nuevaCant > 0 ? { ...it, cantidad: nuevaCant } : null;
                }
                return it;
            }).filter(Boolean);

            return {
                ...prev,
                [idMesa]: nuevoCart
            };
        });
    }, [mesaSeleccionada]);

    // Remover producto del carrito
    const removerDelCarritoMesa = useCallback((idProducto) => {
        if (!mesaSeleccionada) return;
        const idMesa = mesaSeleccionada.idMesa;

        setCarritosPorMesa(prev => {
            const actualCart = prev[idMesa] || [];
            return {
                ...prev,
                [idMesa]: actualCart.filter(it => it.idProducto !== idProducto)
            };
        });
    }, [mesaSeleccionada]);

    // Cambiar nota específica de un producto en el carrito
    const cambiarNotaItem = (idProducto, nota) => {
        if (!mesaSeleccionada) return;
        const idMesa = mesaSeleccionada.idMesa;

        setCarritosPorMesa(prev => {
            const actualCart = prev[idMesa] || [];
            return {
                ...prev,
                [idMesa]: actualCart.map(it =>
                    it.idProducto === idProducto ? { ...it, nota } : it
                )
            };
        });
    };

    // Enviar comanda a Cocina (KDS)
    const handleEnviarComandaCocina = async () => {
        if (!mesaSeleccionada) return;
        if (cartActual.length === 0) {
            toast.warn('Agrega al menos un producto a la comanda');
            return;
        }

        setGuardandoPedido(true);
        try {
            const usuarioActual = JSON.parse(localStorage.getItem('usuario') || '{}');

            const payload = {
                idMesa: mesaSeleccionada.idMesa,
                nombre: nombreCliente.trim() || `Cliente ${mesaSeleccionada.mesa}`,
                nota: notaCocinaGeneral.trim(),
                estado: 'Pendiente', // Va directo a KDS (Cocina)
                origen: 'mesero',
                tipoEntrega: 'mesa',
                metodoPago: 'efectivo',
                estadoPago: 'pendiente',
                idUsuario: usuarioActual.idUsuario || null,
                total: totalDineroCart,
                productos: JSON.stringify(cartActual)
            };

            const res = await fetch(`${baseURL}/pedidoPost.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            setGuardandoPedido(false);

            if (data.idPedido || data.mensaje) {
                const idPed = data.idPedido || '';
                Swal.fire({
                    title: '¡Comanda enviada a Cocina!',
                    html: `Orden <b>#${idPed}</b> enviada correctamente para la <b>${mesaSeleccionada.mesa}</b>.<br><br>🍳 El equipo de cocina ya la tiene en su pantalla KDS.`,
                    icon: 'success',
                    confirmButtonColor: '#10b981',
                    confirmButtonText: 'Aceptar'
                });

                // Limpiar el carrito de esta mesa
                setCarritosPorMesa(prev => ({
                    ...prev,
                    [mesaSeleccionada.idMesa]: []
                }));
                setNotaCocinaGeneral('');
                setDrawerComandaAbierto(false);

                // Refrescar mesas y pedidos
                await cargarPedidos();
                await cargarMesas();
            } else {
                toast.error(data.error || 'Error al guardar el pedido');
            }
        } catch (e) {
            setGuardandoPedido(false);
            console.error(e);
            toast.error('Error de conexión al enviar comanda a cocina');
        }
    };

    // Liberar / Desocupar mesa
    const handleLiberarMesa = useCallback(async (mesaObj) => {
        const pedidosActivos = pedidos.filter(p => p.idMesa === mesaObj.idMesa && p.estado !== 'Pagado' && p.estado !== 'Rechazado');
        if (pedidosActivos.length > 0) {
            const totalPendiente = pedidosActivos.reduce((acc, p) => acc + (parseFloat(p.total) || 0), 0);
            Swal.fire({
                title: 'Mesa con consumos activos',
                html: `La <b>${mesaObj.mesa}</b> tiene <b>${pedidosActivos.length}</b> pedido(s) activo(s) o pendiente(s) de cobro (Total: <b>${moneda} ${totalPendiente.toFixed(2)}</b>).<br><br>⚠️ Todos los pedidos deben cobrarse en el punto de venta (POS) antes de desocupar la mesa.`,
                icon: 'warning',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#f59e0b'
            });
            return;
        }

        const confirmacion = await Swal.fire({
            title: `¿Liberar ${mesaObj.mesa}?`,
            text: `El servicio ha finalizado. La mesa pasará a estado disponible para nuevos clientes.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, liberar mesa',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#10b981',
            cancelButtonColor: '#64748b'
        });

        if (!confirmacion.isConfirmed) return;

        try {
            const res = await fetch(`${baseURL}/mesaLiberar.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idMesa: mesaObj.idMesa })
            });
            const data = await res.json();

            if (data.exito) {
                Swal.fire({
                    title: '¡Mesa liberada!',
                    text: `${mesaObj.mesa} ahora se encuentra disponible.`,
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
                cargarMesas();
                cargarPedidos();
            } else {
                Swal.fire({
                    title: 'No se puede liberar',
                    text: data.mensaje || data.error || 'Ocurrió un error al intentar liberar la mesa.',
                    icon: 'error'
                });
            }
        } catch (e) {
            console.error(e);
            toast.error('Error de red al liberar la mesa');
        }
    }, [pedidos, cargarMesas, cargarPedidos]);

    // Obtener pedidos activos para una mesa específica
    const obtenerPedidosMesa = useCallback((idMesa) => {
        return pedidos.filter(p => p.idMesa === idMesa && p.estado !== 'Pagado' && p.estado !== 'Rechazado');
    }, [pedidos]);

    // Recargar manual completa
    const handleRecargarManual = () => {
        cargarLlamadas();
        cargarMesas();
        cargarPedidos();
        cargarProductos();
        cargarCategorias();
    };

    return (
        <div className="meseros-container">
            <ToastContainer />

            {/* VISTA 1: PANTALLA INICIAL - SELECCIÓN DE MESAS */}
            {vistaActiva === 'mesas' && (
                <div className="mesas-view-wrapper">
                    {/* Topbar del panel */}
                    <header className="meseros-topbar">
                        <div className="meseros-title">
                            <h2><FontAwesomeIcon icon={faUserTie} /> Panel del Mesero</h2>
                            <span>Atención táctil móvil para mesas y registro ágil de comandas</span>
                        </div>
                        <button
                            type="button"
                            className="meseros-reload-btn"
                            onClick={handleRecargarManual}
                            title="Actualizar datos completos"
                        >
                            <FontAwesomeIcon icon={faSync} /> Actualizar
                        </button>
                    </header>

                    {/* SECCIÓN: Solicitudes y Llamadas Activas de Clientes */}
                    <section className="meseros-llamadas-box">
                        <div className="llamadas-box-header">
                            <div className="llamadas-title-flex">
                                <FontAwesomeIcon icon={faBell} className="bell-pulse" />
                                <h3>Llamadas de Clientes en Mesa</h3>
                            </div>
                            <span className={`llamadas-count-badge ${llamadas.length > 0 ? 'badge-alert' : ''}`}>
                                {llamadas.length} {llamadas.length === 1 ? 'pendiente' : 'pendientes'}
                            </span>
                        </div>

                        {llamadas.length === 0 ? (
                            <div className="no-llamadas-msg">
                                <FontAwesomeIcon icon={faCheckCircle} />
                                <p>No hay solicitudes pendientes de mesas en este momento.</p>
                            </div>
                        ) : (
                            <div className="llamadas-grid">
                                {llamadas.map(ll => (
                                    <div key={ll.idLlamada} className="llamada-card">
                                        <div className="llamada-card-header">
                                            <span className="llamada-mesa-badge">🍽️ {ll.mesa}</span>
                                            <span className="llamada-time">
                                                <FontAwesomeIcon icon={faClock} /> {ll.createdAt?.split(' ')[1] || ll.createdAt}
                                            </span>
                                        </div>
                                        <div className="llamada-motivo">
                                            <strong>Motivo:</strong> {ll.motivo || 'Atención en mesa'}
                                        </div>
                                        <div className="llamada-actions">
                                            <button
                                                type="button"
                                                className="btn-abrir-mesa-llamada"
                                                onClick={() => {
                                                    atenderLlamada(ll.idLlamada);
                                                    abrirMesaPorId(ll.idMesa);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faUtensils} /> Atender en Mesa
                                            </button>
                                            <button
                                                type="button"
                                                className="btn-atender-llamada"
                                                onClick={() => atenderLlamada(ll.idLlamada)}
                                                title="Marcar como atendida sin abrir"
                                            >
                                                <FontAwesomeIcon icon={faCheck} /> Listo
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    {/* SECCIÓN: Mesas del Restaurante y Estado */}
                    <section className="meseros-mesas-section">
                        <div className="mesas-sec-header">
                            <div>
                                <h3>🍽️ Selecciona una Mesa</h3>
                                <p>Toca una mesa para abrir su carta digital y tomar comanda o revisar consumos</p>
                            </div>
                        </div>

                        <div className="meseros-mesas-grid">
                            {mesas.map(m => {
                                const pedidosActivosMesa = obtenerPedidosMesa(m.idMesa);
                                const tieneLlamada = llamadas.some(l => l.idMesa === m.idMesa);
                                const llamadaActiva = llamadas.find(l => l.idMesa === m.idMesa);
                                const estaOcupada = m.estado !== 'libre';
                                const itemsEnCarrito = (carritosPorMesa[m.idMesa] || []).reduce((acc, i) => acc + i.cantidad, 0);

                                return (
                                    <MeseroMesaCard
                                        key={m.idMesa}
                                        m={m}
                                        pedidosActivosMesa={pedidosActivosMesa}
                                        tieneLlamada={tieneLlamada}
                                        llamadaActiva={llamadaActiva}
                                        estaOcupada={estaOcupada}
                                        itemsEnCarrito={itemsEnCarrito}
                                        onSeleccionar={seleccionarMesa}
                                        onLiberar={handleLiberarMesa}
                                    />
                                );
                            })}
                        </div>
                    </section>
                </div>
            )}

            {/* VISTA 2: MENÚ DIGITAL Y COMANDA DE LA MESA SELECCIONADA */}
            {vistaActiva === 'menu' && mesaSeleccionada && (
                <div className="mesero-digital-menu-wrapper">
                    {/* Barra superior de navegación y estado de mesa */}
                    <div className="mesero-menu-topbar">
                        <button type="button" className="btn-volver-mesas" onClick={volverAMesas}>
                            <FontAwesomeIcon icon={faArrowLeft} /> Volver a Mesas
                        </button>

                        <div className="mesero-table-info-pill">
                            <span className="table-pill-title">🍽️ {mesaSeleccionada.mesa}</span>
                            <span className={`table-pill-status ${mesaSeleccionada.estado !== 'libre' ? 'status-ocupada' : 'status-libre'}`}>
                                {mesaSeleccionada.estado !== 'libre' ? 'Ocupada' : 'Disponible'}
                            </span>
                        </div>

                        {obtenerPedidosMesa(mesaSeleccionada.idMesa).length > 0 && (
                            <button
                                type="button"
                                className="btn-ver-consumos-activos"
                                onClick={() => setModalVerConsumo(true)}
                                title="Ver pedidos ya registrados en esta mesa"
                            >
                                <FontAwesomeIcon icon={faReceipt} /> Cuenta: {moneda} {obtenerPedidosMesa(mesaSeleccionada.idMesa).reduce((acc, p) => acc + (parseFloat(p.total) || 0), 0).toFixed(2)}
                            </button>
                        )}
                    </div>

                    {/* Banner informativo si la mesa ya está ocupada */}
                    {obtenerPedidosMesa(mesaSeleccionada.idMesa).length > 0 && (
                        <div className="mesero-cuenta-existente-banner">
                            <FontAwesomeIcon icon={faExclamationCircle} />
                            <div>
                                <strong>Mesa con consumos activos:</strong> Los nuevos platos o bebidas se enviarán a cocina como comanda adicional para sumarse a la cuenta de esta mesa.
                            </div>
                        </div>
                    )}

                    {/* Barra de Búsqueda */}
                    <div className="mesero-search-bar-wrap">
                        <div className="mesero-search-box">
                            <FontAwesomeIcon icon={faSearch} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Buscar plato, bebida, postre..."
                                value={busqueda}
                                onChange={(e) => setBusqueda(e.target.value)}
                            />
                            {busqueda && (
                                <button type="button" className="clear-search-btn" onClick={() => setBusqueda('')}>
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Barra de Categorías con desplazamiento táctil horizontal */}
                    <div className="mesero-categories-scroll-wrapper">
                        <div className="mesero-categories-track">
                            <button
                                type="button"
                                className={`mesero-cat-chip ${categoriaActiva === 'todas' ? 'active' : ''}`}
                                onClick={() => setCategoriaActiva('todas')}
                            >
                                Todo
                            </button>
                            {categoriasDisponibles.map(cat => (
                                <button
                                    type="button"
                                    key={cat.idCategoria}
                                    className={`mesero-cat-chip ${String(categoriaActiva) === String(cat.idCategoria) ? 'active' : ''}`}
                                    onClick={() => setCategoriaActiva(String(cat.idCategoria))}
                                >
                                    {cat.categoria}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Sección Más Vendidos / Populares (solo si no hay búsqueda activa y está en 'todas') */}
                    {!busqueda && categoriaActiva === 'todas' && masVendidos.length > 0 && (
                        <section className="mesero-bestsellers-section">
                            <div className="section-title-wrap">
                                <h3><FontAwesomeIcon icon={faFire} style={{ color: '#f59e0b' }} /> Más Vendidos</h3>
                            </div>
                            <div className="mesero-bestsellers-track">
                                {masVendidos.map(prod => (
                                    <MeseroBestsellerCard
                                        key={prod.idProducto}
                                        prod={prod}
                                        usarPrecios={usarPrecios}
                                        cantidadEnCart={cantidadesCartMap[prod.idProducto] || 0}
                                        onAgregar={agregarAlCarritoMesa}
                                        onModificar={modificarCantidadMesa}
                                    />
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Catálogo Principal de Platos y Bebidas */}
                    <main className="mesero-catalog-section">
                        <div className="section-title-wrap">
                            <h3>
                                {categoriaActiva === 'todas'
                                    ? (busqueda ? `Resultados para "${busqueda}"` : 'Toda la Carta')
                                    : (mapaCategorias[String(categoriaActiva)] || 'Categoría')
                                }
                            </h3>
                            <span>{productosFiltrados.length} productos</span>
                        </div>

                        {productosFiltrados.length === 0 ? (
                            <div className="mesero-empty-catalog">
                                <FontAwesomeIcon icon={faUtensils} />
                                <p>No se encontraron productos en esta sección.</p>
                            </div>
                        ) : (
                            <div className="mesero-dishes-grid">
                                {productosFiltrados.map(prod => (
                                    <MeseroDishCard
                                        key={prod.idProducto}
                                        prod={prod}
                                        categoriaNombre={mapaCategorias[String(prod.idCategoria)] || ''}
                                        usarPrecios={usarPrecios}
                                        cantidadEnCart={cantidadesCartMap[prod.idProducto] || 0}
                                        onAgregar={agregarAlCarritoMesa}
                                        onModificar={modificarCantidadMesa}
                                    />
                                ))}
                            </div>
                        )}
                    </main>

                    {/* Barra Flotante Inferior de Comanda / Carrito */}
                    {totalItemsCart > 0 && (
                        <div
                            className="mesero-floating-cart-bar"
                            onClick={() => setDrawerComandaAbierto(true)}
                        >
                            <div className="floating-cart-left">
                                <span className="floating-cart-badge">{totalItemsCart}</span>
                                <span className="floating-cart-title">Ver Comanda ({mesaSeleccionada.mesa})</span>
                            </div>

                            <div className="floating-cart-right">
                                {usarPrecios && (
                                    <span className="floating-cart-total">{moneda} {totalDineroCart.toFixed(2)}</span>
                                )}
                                <FontAwesomeIcon icon={faArrowRight} className="floating-cart-arrow" />
                            </div>
                        </div>
                    )}

                    {/* Modal / Drawer para Detallar Comanda y Enviar a Cocina */}
                    {drawerComandaAbierto && (
                        <div className="mesero-drawer-overlay" onClick={() => setDrawerComandaAbierto(false)}>
                            <div className="mesero-drawer-content" onClick={(e) => e.stopPropagation()}>
                                <div className="drawer-header">
                                    <div>
                                        <h3>📝 Comanda: {mesaSeleccionada.mesa}</h3>
                                        <span className="drawer-sub">
                                            {totalItemsCart} {totalItemsCart === 1 ? 'producto seleccionado' : 'productos seleccionados'}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="drawer-close-btn"
                                        onClick={() => setDrawerComandaAbierto(false)}
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </div>

                                <div className="drawer-body">
                                    {/* Lista de productos de la comanda */}
                                    <div className="drawer-cart-items">
                                        {cartActual.map(item => (
                                            <div key={item.idProducto} className="drawer-cart-item-card">
                                                <div className="drawer-item-top">
                                                    <div className="drawer-item-info">
                                                        <span className="drawer-item-title">{item.titulo}</span>
                                                        {usarPrecios && (
                                                            <span className="drawer-item-price">{moneda} {item.precio} c/u</span>
                                                        )}
                                                    </div>

                                                    <div className="mesero-qty-stepper">
                                                        <button type="button" onClick={() => modificarCantidadMesa(item.idProducto, -1)}>-</button>
                                                        <span className="stepper-val">{item.cantidad}</span>
                                                        <button type="button" onClick={() => modificarCantidadMesa(item.idProducto, 1)}>+</button>
                                                    </div>

                                                    {usarPrecios && (
                                                        <span className="drawer-item-subtotal">
                                                            {moneda} {(item.precio * item.cantidad).toFixed(2)}
                                                        </span>
                                                    )}

                                                    <button
                                                        type="button"
                                                        className="drawer-remove-item-btn"
                                                        onClick={() => removerDelCarritoMesa(item.idProducto)}
                                                        title="Eliminar de comanda"
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </div>

                                                {/* Observación específica por plato/bebida */}
                                                <div className="drawer-item-note-row">
                                                    <FontAwesomeIcon icon={faCommentDots} />
                                                    <input
                                                        type="text"
                                                        placeholder="Detalle específico (ej: sin cebolla, término medio, hielo aparte)"
                                                        value={item.nota || ''}
                                                        onChange={(e) => cambiarNotaItem(item.idProducto, e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Campos de Nombre y Observaciones Generales de Cocina */}
                                    <div className="drawer-inputs-box">
                                        <div className="drawer-field">
                                            <label>Nombre del cliente en mesa (opcional):</label>
                                            <input
                                                type="text"
                                                placeholder="Ej: Familia Gómez / Mesa 4"
                                                value={nombreCliente}
                                                onChange={(e) => setNombreCliente(e.target.value)}
                                            />
                                        </div>

                                        <div className="drawer-field">
                                            <label>Observaciones generales para cocina / KDS:</label>
                                            <textarea
                                                rows="2"
                                                placeholder="Ej: Servir bebidas primero, comensal alérgico al maní..."
                                                value={notaCocinaGeneral}
                                                onChange={(e) => setNotaCocinaGeneral(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    {/* Resumen y Botón de Enviar a Cocina */}
                                    {usarPrecios && (
                                        <div className="drawer-total-box">
                                            <span>Total Comanda:</span>
                                            <strong>{moneda} {totalDineroCart.toFixed(2)}</strong>
                                        </div>
                                    )}

                                    <div className="drawer-footer-actions">
                                        <button
                                            type="button"
                                            className="btn-enviar-comanda-grande"
                                            onClick={handleEnviarComandaCocina}
                                            disabled={cartActual.length === 0 || guardandoPedido}
                                        >
                                            {guardandoPedido ? (
                                                <span>Enviando comanda a cocina...</span>
                                            ) : (
                                                <span>🍳 Enviar Pedido a Cocina (KDS)</span>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Modal para ver consumos / pedidos ya activos de la mesa */}
                    {modalVerConsumo && (
                        <div className="modal-consumos-overlay" onClick={() => setModalVerConsumo(false)}>
                            <div className="modal-consumos-content" onClick={(e) => e.stopPropagation()}>
                                <div className="modal-consumos-header">
                                    <h3>📑 Consumo Acumulado - {mesaSeleccionada.mesa}</h3>
                                    <button type="button" className="close-btn" onClick={() => setModalVerConsumo(false)}>
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </div>

                                <div className="modal-consumos-body">
                                    {obtenerPedidosMesa(mesaSeleccionada.idMesa).length === 0 ? (
                                        <p style={{ textAlign: 'center', color: '#64748b', padding: '20px' }}>
                                            No hay pedidos activos registrados en esta mesa.
                                        </p>
                                    ) : (
                                        obtenerPedidosMesa(mesaSeleccionada.idMesa).map(ped => {
                                            let items = [];
                                            try {
                                                items = typeof ped.productos === 'string' ? JSON.parse(ped.productos) : (ped.productos || []);
                                            } catch (e) {
                                                items = [];
                                            }

                                            return (
                                                <div key={ped.idPedido} className="consumo-pedido-card">
                                                    <div className="consumo-pedido-head">
                                                        <div>
                                                            <strong>Orden #{ped.idPedido}</strong>
                                                            <span className="consumo-time"> ({ped.createdAt?.split(' ')[1] || ''})</span>
                                                        </div>
                                                        <span className="consumo-status-badge">{ped.estado}</span>
                                                    </div>

                                                    <div className="consumo-items-list">
                                                        {items.map((it, idx) => (
                                                            <div key={idx} className="consumo-item-row">
                                                                <span>{it.cantidad}x {it.titulo}</span>
                                                                {it.nota && <span className="consumo-item-note">({it.nota})</span>}
                                                                {usarPrecios && <span>{moneda} {(it.precio * it.cantidad).toFixed(2)}</span>}
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {ped.nota && (
                                                        <div className="consumo-general-note">
                                                            <strong>Nota:</strong> {ped.nota}
                                                        </div>
                                                    )}

                                                    {usarPrecios && (
                                                        <div className="consumo-subtotal-row">
                                                            <span>Subtotal Orden:</span>
                                                            <strong>{moneda} {Number(ped.total).toFixed(2)}</strong>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}

                                    {usarPrecios && obtenerPedidosMesa(mesaSeleccionada.idMesa).length > 0 && (
                                        <div className="consumo-total-general">
                                            <span>Gran Total Mesa:</span>
                                            <strong>
                                                {moneda} {obtenerPedidosMesa(mesaSeleccionada.idMesa).reduce((acc, p) => acc + (parseFloat(p.total) || 0), 0).toFixed(2)}
                                            </strong>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
